/**
 * SkillPermissionsSelector
 *
 * Compact, searchable skill picker for the workflow AI node editor.
 * Lets admins restrict which AI skills a specific workflow node may use,
 * independently of the site-wide subscription and override settings.
 *
 * Three states (via permittedSkills prop):
 *   null    = "All enabled skills" — no restriction (legacy default for existing nodes).
 *   []      = "No skills" — AI has no tools available.
 *   [...]   = Only the listed skill names are offered to the AI at runtime.
 *
 * The filter is applied AFTER subscription gate and skill override checks —
 * the most restrictive setting always wins.
 */

import React, { useState } from "react";
import { SkillDef } from "./SkillControlsPanel";

interface Props {
  /** All skills for the site's enabled integrations (passed from parent). */
  availableSkills: SkillDef[];
  /**
   * Currently permitted tool names.
   *   null    = all skills (no restriction)
   *   []      = no skills (AI has no tools)
   *   [...]   = only these skills
   */
  permittedSkills: string[] | null;
  /** Called when the selection changes. null = allow all, [] = none, [...] = specific. */
  onChange: (skills: string[] | null) => void;
}

type SkillMode = "all" | "none" | "selected";

function getMode(permittedSkills: string[] | null): SkillMode {
  if (permittedSkills === null) return "all";
  if (permittedSkills.length === 0) return "none";
  return "selected";
}

export default function SkillPermissionsSelector({
  availableSkills,
  permittedSkills,
  onChange,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const mode = getMode(permittedSkills);
  const selected = permittedSkills ?? [];

  const isAllowed = (name: string) =>
    mode === "all" || selected.includes(name);

  const filtered = availableSkills.filter(
    (s) =>
      !search ||
      s.label.toLowerCase().includes(search.toLowerCase()) ||
      s.name.toLowerCase().includes(search.toLowerCase())
  );

  const groups = Array.from(new Set(filtered.map((s) => s.group ?? "General")));

  function toggle(name: string) {
    if (mode === "all") {
      // Switching from "all" to explicit — start with everything except this one
      onChange(availableSkills.map((s) => s.name).filter((n) => n !== name));
    } else if (mode === "none") {
      onChange([name]);
    } else if (selected.includes(name)) {
      const next = selected.filter((n) => n !== name);
      // If nothing left, go to "none" state (empty array = no skills)
      onChange(next.length > 0 ? next : []);
    } else {
      onChange([...selected, name]);
    }
  }

  function resetToAll() {
    onChange(null);
    setSearch("");
  }

  function disableAll() {
    onChange([]);
    setSearch("");
  }

  function selectAll() {
    onChange(filtered.map((s) => s.name));
  }

  const summaryPill = () => {
    if (mode === "all") {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => e.key === "Enter" && setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 cursor-pointer hover:bg-slate-100 text-sm text-slate-500 select-none"
        >
          <span className="text-green-500">✓</span>
          <span>All enabled skills</span>
          <span className="text-xs text-slate-400 ml-auto">click to restrict</span>
        </div>
      );
    }
    if (mode === "none") {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => e.key === "Enter" && setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-slate-100 cursor-pointer hover:bg-slate-200 text-sm select-none"
        >
          <span className="text-slate-400">⊘</span>
          <span className="font-semibold text-slate-600">No skills</span>
          <span className="text-xs text-slate-400 ml-auto">click to add</span>
        </div>
      );
    }
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => e.key === "Enter" && setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 cursor-pointer hover:bg-amber-100 text-sm select-none"
      >
        <span className="font-semibold text-amber-800">
          {selected.length}{" "}
          {selected.length === 1 ? "skill" : "skills"} permitted
        </span>
        <span className="text-amber-600 text-xs ml-auto">click to edit</span>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-700">
          Permitted Skills
        </label>
        <div className="flex items-center gap-2">
          {mode !== "all" && (
            <button
              type="button"
              onClick={resetToAll}
              className="text-xs text-blue-600 hover:underline"
            >
              Allow all
            </button>
          )}
          {mode !== "none" && (
            <button
              type="button"
              onClick={disableAll}
              className="text-xs text-slate-500 hover:text-red-600 hover:underline"
            >
              Disable all
            </button>
          )}
        </div>
      </div>

      {summaryPill()}

      {open && (
        <div className="rounded-lg border border-slate-200 overflow-hidden shadow-sm">
          {/* Search + bulk actions */}
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <input
              type="text"
              placeholder="Search skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-xs px-3 py-1.5 rounded border border-slate-200 focus:outline-none focus:border-blue-400"
              autoFocus
            />
            <button
              type="button"
              onClick={selectAll}
              className="text-xs text-slate-500 hover:text-blue-600 whitespace-nowrap"
              title="Select all visible skills"
            >
              Select all
            </button>
            {mode !== "none" && (
              <button
                type="button"
                onClick={disableAll}
                className="text-xs text-slate-500 hover:text-red-600 whitespace-nowrap"
                title="Deselect all skills"
              >
                Deselect all
              </button>
            )}
          </div>

          {/* Skill list grouped by integration */}
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {groups.length === 0 && (
              <div className="px-4 py-3 text-xs text-slate-400">
                No skills match your search.
              </div>
            )}
            {groups.map((group) => (
              <div key={group}>
                <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {group}
                </div>
                {filtered
                  .filter((s) => (s.group ?? "General") === group)
                  .map((skill) => {
                    const checked = isAllowed(skill.name);
                    return (
                      <div key={skill.name}>
                        <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(skill.name)}
                            className="rounded border-slate-300 text-blue-600"
                          />
                          <span className="text-xs text-slate-700 font-medium">
                            {skill.label}
                          </span>
                          {skill.destructive && (
                            <span className="text-[9px] font-bold uppercase bg-red-100 text-red-600 px-1 rounded ml-1">
                              Destructive
                            </span>
                          )}
                          <span className="text-[10px] font-mono text-slate-300 ml-auto truncate">
                            {skill.name}
                          </span>
                        </label>
                        {checked && skill.channelWarning && (
                          <div className="mx-3 mb-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800 leading-relaxed">
                            <span className="font-semibold">IVR/SMS note: </span>
                            {skill.channelWarning}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-2 border-t border-slate-100 flex justify-between items-center">
            <span className="text-[10px] text-slate-400">
              {mode === "all" && "All skills enabled — uncheck to restrict"}
              {mode === "none" && "No skills — check to enable individual skills"}
              {mode === "selected" && `${selected.length} of ${availableSkills.length} skills enabled`}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {mode === "none" && (
        <p className="text-[10px] text-slate-500">
          This AI node has no skills — the AI will respond from its prompt only, with no tools.
        </p>
      )}
      {mode === "selected" && (
        <p className="text-[10px] text-amber-700">
          Only the {selected.length} selected{" "}
          {selected.length === 1 ? "skill" : "skills"} will be available
          to this AI node at runtime, even if more skills are enabled site-wide.
        </p>
      )}
    </div>
  );
}
